import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Users, User, Tag, Share2, CheckCircle, Brain, ArrowRight, Zap, Timer } from "lucide-react";
import { trackPageView } from "../hooks/useAnalytics";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Countdown Timer Component
function CountdownTimer({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;

      if (diff <= 0) {
        setIsExpired(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  if (isExpired) return null;

  return (
    <div className="bg-gradient-to-r from-violet-600/20 to-purple-600/20 border border-violet-500/30 p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Timer size={16} className="text-violet-400" />
        <span className="text-violet-300 text-xs font-bold uppercase tracking-wide">Registration Closes In</span>
      </div>
      <div className="flex justify-center gap-3">
        <div className="text-center">
          <div className="bg-black/40 text-white font-outfit font-bold text-2xl px-3 py-2 min-w-12">
            {String(timeLeft.days).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[10px] mt-1 font-dm-sans uppercase">Days</div>
        </div>
        <div className="text-violet-400 font-bold text-2xl">:</div>
        <div className="text-center">
          <div className="bg-black/40 text-white font-outfit font-bold text-2xl px-3 py-2 min-w-12">
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[10px] mt-1 font-dm-sans uppercase">Hours</div>
        </div>
        <div className="text-violet-400 font-bold text-2xl">:</div>
        <div className="text-center">
          <div className="bg-black/40 text-white font-outfit font-bold text-2xl px-3 py-2 min-w-12">
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[10px] mt-1 font-dm-sans uppercase">Mins</div>
        </div>
        <div className="text-violet-400 font-bold text-2xl">:</div>
        <div className="text-center">
          <div className="bg-black/40 text-white font-outfit font-bold text-2xl px-3 py-2 min-w-12">
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <div className="text-violet-400 text-[10px] mt-1 font-dm-sans uppercase">Secs</div>
        </div>
      </div>
    </div>
  );
}

// Smart Seat Display Component
function SmartSeatDisplay({ seatsFilled, totalSeats }) {
  const fillRate = totalSeats > 0 ? (seatsFilled / totalSeats) * 100 : 0;
  const seatsLeft = totalSeats - seatsFilled;
  
  // Fully booked
  if (seatsLeft <= 0) {
    return (
      <div className="flex items-center gap-3">
        <Users size={16} className="text-red-400 flex-shrink-0" />
        <div>
          <span className="text-zinc-500 text-xs font-dm-sans">Availability</span>
          <p className="text-red-400 text-sm font-bold font-dm-sans">Fully Booked</p>
        </div>
      </div>
    );
  }
  
  // > 80% filled - Show urgency with exact seats left
  if (fillRate >= 80) {
    return (
      <div className="flex items-center gap-3">
        <div className="relative">
          <Users size={16} className="text-red-400 flex-shrink-0" />
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
        </div>
        <div>
          <span className="text-zinc-500 text-xs font-dm-sans">Almost Full!</span>
          <p className="text-red-400 text-sm font-bold font-dm-sans animate-pulse">
            🔥 Only {seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left!
          </p>
        </div>
      </div>
    );
  }
  
  // 50-80% filled - Show "Filling Fast"
  if (fillRate >= 50) {
    return (
      <div className="flex items-center gap-3">
        <Zap size={16} className="text-amber-400 flex-shrink-0" />
        <div>
          <span className="text-zinc-500 text-xs font-dm-sans">Availability</span>
          <p className="text-amber-400 text-sm font-bold font-dm-sans">
            ⚡ Filling Fast - Register Now
          </p>
        </div>
      </div>
    );
  }
  
  // < 50% filled - Show positive message without numbers
  return (
    <div className="flex items-center gap-3">
      <Users size={16} className="text-[#00F0FF] flex-shrink-0" />
      <div>
        <span className="text-zinc-500 text-xs font-dm-sans">Batch Size</span>
        <p className="text-[#00F0FF] text-sm font-medium font-dm-sans">
          Limited Seats · Small Batch Training
        </p>
      </div>
    </div>
  );
}

export default function TrainingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/trainings/${id}`)
      .then(r => {
        setTraining(r.data);
        // Track page view with training ID
        trackPageView(`/trainings/${id}`, id);
      })
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleShare = () => {
    const url = training?.share_link || window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="marketing-bg min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!training) return null;

  const seatsLeft = training.seats - (training.seats_filled || 0);
  const isFull = seatsLeft <= 0;
  
  // Offer calculations
  const hasOffer = training.offer_enabled && training.offer_percentage && training.offer_end_date;
  const offerEndDate = hasOffer ? new Date(training.offer_end_date) : null;
  const isOfferValid = hasOffer && offerEndDate && offerEndDate >= new Date();
  const offerPrice = isOfferValid ? Math.round(training.price * (1 - training.offer_percentage / 100)) : training.price;

  // Format date and time
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    
    // Parse date - handle both "YYYY-MM-DD" and ISO datetime formats
    let year, monthName, day;
    
    if (dateStr.includes('T')) {
      // ISO datetime format
      const parts = dateStr.split('T')[0].split('-');
      year = parts[0];
      monthName = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, 1)).toLocaleString('en-US', { month: 'short' });
      day = parts[2].padStart(2, '0');
    } else {
      // Simple date format YYYY-MM-DD
      const parts = dateStr.split('-');
      year = parts[0];
      monthName = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, 1)).toLocaleString('en-US', { month: 'short' });
      day = parts[2].padStart(2, '0');
    }
    
    return `${year}-${monthName}-${day}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBA';
    // Ensure time has AM/PM - if it's in 24hr format, convert it
    if (!timeStr.toUpperCase().includes('AM') && !timeStr.toUpperCase().includes('PM')) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
    }
    return timeStr;
  };

  const formattedDate = formatDate(training.date);
  const formattedTime = formatTime(training.time);

  return (
    <div className="marketing-bg min-h-screen">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans"
          >
            <ArrowLeft size={16} /> Back to Courses
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={18} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF]">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-16 px-6 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {/* Course Image */}
              <div className="relative overflow-hidden mb-8 border border-zinc-800">
                <img
                  src={training.poster_image_data || training.image_url || "https://images.unsplash.com/photo-1646583288948-24548aedffd8?w=800&h=400&fit=crop"}
                  alt={training.title}
                  className="w-full h-72 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className={`text-xs font-bold px-3 py-1.5 ${training.status === 'completed' ? 'bg-zinc-600 text-zinc-300' : 'bg-[#00F0FF] text-black'}`}>
                    {training.status === 'completed' ? 'COMPLETED' : 'ENROLLING NOW'}
                  </span>
                  {isOfferValid && (
                    <span className="text-xs font-bold px-3 py-1.5 bg-red-500 text-white animate-pulse">
                      {training.offer_percentage}% OFF
                    </span>
                  )}
                </div>
              </div>

              <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-4 leading-tight">{training.title}</h1>
              
              <div className="flex items-center gap-2 mb-6">
                <button
                  data-testid="share-btn"
                  onClick={handleShare}
                  className="flex items-center gap-2 border border-zinc-800 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] text-sm px-4 py-2 transition-colors font-dm-sans"
                >
                  {copied ? <CheckCircle size={15} className="text-[#00F0FF]" /> : <Share2 size={15} />}
                  {copied ? "Link Copied!" : "Share Training"}
                </button>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-8">
                <h2 className="font-outfit font-bold text-xl text-white mb-3">About This Training</h2>
                <p className="text-[#A1A1AA] leading-relaxed font-dm-sans text-base">{training.description}</p>
              </div>

              {/* What You'll Learn */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-8">
                <h2 className="font-outfit font-bold text-xl text-white mb-4">What You'll Gain</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["Hands-on AI project experience", "Industry-recognized certificate", "Expert instructor guidance", "Practical AI implementation skills", "Networking with AI professionals", "Access to training materials"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-[#00F0FF] flex-shrink-0" />
                      <span className="text-[#A1A1AA] text-sm font-dm-sans">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar Card */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="sticky top-24 bg-zinc-900/80 border border-zinc-800 hover:border-[#00F0FF]/30 transition-all duration-300 p-6"
            >
              {/* Offer Badge */}
              {isOfferValid && (
                <div className="bg-red-500 text-white text-center py-2 px-4 mb-4 -mx-6 -mt-6">
                  <span className="font-bold text-sm">{training.offer_percentage}% OFF</span>
                  <span className="text-xs ml-2 opacity-90">
                    Ends {offerEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              )}
              
              <div className="mb-6">
                {isOfferValid ? (
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#00F0FF] font-outfit font-black text-4xl">&#8377;{offerPrice.toLocaleString('en-IN')}</span>
                      <span className="text-zinc-500 font-outfit text-xl line-through">&#8377;{training.price.toLocaleString('en-IN')}</span>
                    </div>
                    <span className="text-green-400 text-sm font-dm-sans font-medium">
                      You save ₹{(training.price - offerPrice).toLocaleString('en-IN')}
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="text-[#00F0FF] font-outfit font-black text-4xl">&#8377;{training.price.toLocaleString('en-IN')}</span>
                    <span className="text-[#A1A1AA] text-sm ml-2 font-dm-sans">per person</span>
                  </>
                )}
              </div>

              {/* Countdown Timer */}
              {training.status !== 'completed' && !isFull && (
                <CountdownTimer targetDate={training.date} />
              )}

              <div className="space-y-4 mb-6 pb-6 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <span className="text-zinc-500 text-xs font-dm-sans">Date</span>
                    <p className="text-white text-sm font-medium font-dm-sans">{formattedDate}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <span className="text-zinc-500 text-xs font-dm-sans">Time</span>
                    <p className="text-white text-sm font-medium font-dm-sans">{formattedTime}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag size={16} className="text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <span className="text-zinc-500 text-xs font-dm-sans">Duration</span>
                    <p className="text-white text-sm font-medium font-dm-sans">{training.duration}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User size={16} className="text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <span className="text-zinc-500 text-xs font-dm-sans">Instructor</span>
                    <p className="text-white text-sm font-medium font-dm-sans">{training.instructor}</p>
                  </div>
                </div>
                
                {/* Smart Seat Display */}
                <SmartSeatDisplay 
                  seatsFilled={training.seats_filled || 0} 
                  totalSeats={training.seats} 
                />
              </div>

              <button
                data-testid="register-btn"
                onClick={() => !isFull && navigate(`/trainings/${id}/register`)}
                disabled={isFull || training.status === 'completed'}
                className={`w-full font-outfit font-bold text-lg py-4 flex items-center justify-center gap-2 transition-colors duration-300 ${
                  isFull || training.status === 'completed'
                    ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                    : 'bg-[#00F0FF] text-black hover:bg-white'
                }`}
              >
                {isFull ? 'Fully Booked' : training.status === 'completed' ? 'Training Completed' : 'Register Now'} 
                {!isFull && training.status !== 'completed' && <ArrowRight size={20} />}
              </button>

              <p className="text-[#A1A1AA] text-xs text-center mt-3 font-dm-sans">Secure payment powered by Cashfree</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
